import { useEffect, useRef, useState } from "react";
import { useStore } from "../state/store.js";
import { Avatar } from "./Avatar.js";

interface Hit {
  group: string;
  title: string;
  sub: string;
  avatar?: { name: string; photo?: string | null; photoPos?: string | null; initials?: string | null; color?: string | null };
  icon?: string;
  go: () => void;
}

// Search is the only thing in the page header (spec section 3); the cluster
// itself is one live element the shell re-parents between the header and
// the mobile bar (spec section 9, exception 1).
export function SearchCluster() {
  const open = useStore((s) => s.searchOpen);
  const setOpen = useStore((s) => s.setSearchOpen);
  const experts = useStore((s) => s.experts);
  const nav = useStore((s) => s.nav);
  const openSheet = useStore((s) => s.openSheet);
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setQ("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open, setOpen]);

  const appState = useStore((s) => s.appState);
  const crossfireList = useStore((s) => s.crossfire);
  const needle = q.trim().toLowerCase();
  const index: Hit[] = [
    ...(experts ?? []).map(
      (e): Hit => ({
        group: "Experts",
        title: e.name,
        sub: e.role ?? e.lens ?? "",
        avatar: e,
        go: () => nav("expert", e.id),
      }),
    ),
    ...Object.values(appState?.cards ?? {}).map(
      (c): Hit => ({
        group: "Practices",
        title: c.do,
        sub:
          c.origin === "user"
            ? "Yours"
            : c.expertId
              ? (appState?.experts[c.expertId]?.short ?? c.expertId)
              : "",
        icon: "✓",
        go: () => openSheet({ kind: "practice", cardId: c.id }),
      }),
    ),
    ...(appState?.topics ?? []).map(
      (t): Hit => ({
        group: "Topics",
        title: t.label,
        sub: t.on ? "Reaching you" : "Filtered",
        icon: "◎",
        go: () => nav("controls"),
      }),
    ),
    ...(crossfireList ?? []).map(
      (x): Hit => ({
        group: "Crossfire",
        title: x.question,
        sub: x.recheckDate ? `re-ask ${x.recheckDate}` : "open question",
        icon: "⇄",
        go: () => nav("crossfire", x.id),
      }),
    ),
  ];
  // The dropdown waits for typing: an empty query shows nothing rather
  // than a prepopulated sample (28 Jul 2026).
  const hits: Hit[] = needle
    ? index.filter((h) => `${h.title} ${h.sub}`.toLowerCase().includes(needle)).slice(0, 24)
    : [];

  let lastGroup = "";
  return (
    <>
      {/* The slot is the cluster's in-flow footprint (40x40): without it
          the absolutely positioned pill hangs from a zero-height anchor
          and sits below the bar's midline. The pill overlays the slot and
          grows leftward from its right edge. */}
      <span className="searchslot" aria-hidden="true" />
      <div
        className={`searchbox${open ? " open" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
      <button
        className="sbtn"
        aria-label="Search"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        ⌕
      </button>
      <input
        ref={inputRef}
        type="text"
        placeholder="Experts, practices, topics"
        aria-label="Search experts, practices, and topics"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <button className="sclose" aria-label="Close search" onClick={() => setOpen(false)}>
        ✕
      </button>
      {open && needle !== "" && (
        <div className="sdrop glass">
          {hits.length === 0 ? (
            <p className="semp">Nothing in your world matches that yet. Try a name, a practice, or a topic.</p>
          ) : (
            hits.map((h, i) => {
              const header = h.group !== lastGroup ? <span className="u">{h.group}</span> : null;
              lastGroup = h.group;
              return (
                <span key={i} style={{ display: "contents" }}>
                  {header}
                  <button className="srow" onClick={h.go}>
                    {h.avatar ? <Avatar e={h.avatar} size="s" /> : <span className="sic">{h.icon}</span>}
                    <span>
                      <strong>{h.title}</strong>
                      <small>{h.sub}</small>
                    </span>
                  </button>
                </span>
              );
            })
          )}
          {/* The quick-jump covers what you already have; these hand the
              query to a surface search, which can go wider behind its
              visible scope toggle (spec section 5, "Search on a surface").
              nav() resets the surface's search state, so the query is set
              after it. */}
          <span className="u">Go deeper</span>
          <button
            className="srow"
            onClick={() => {
              const query = q.trim();
              nav("discover");
              useStore.getState().setDiscoverQ(query);
            }}
          >
            <span className="sic">✳</span>
            <span>
              <strong>Search practices for “{q.trim()}”</strong>
              <small>Everything the people you follow publish, or everyone</small>
            </span>
          </button>
          <button
            className="srow"
            onClick={() => {
              const query = q.trim();
              nav("feed");
              useStore.getState().setFeedQ(query);
            }}
          >
            <span className="sic">≋</span>
            <span>
              <strong>Search the feed for “{q.trim()}”</strong>
              <small>Articles, podcasts, and videos</small>
            </span>
          </button>
          <button
            className="srow"
            onClick={() => {
              const query = q.trim();
              nav("crossfire");
              useStore.getState().setXfQ(query);
            }}
          >
            <span className="sic">⇄</span>
            <span>
              <strong>Search Crossfire for “{q.trim()}”</strong>
              <small>Where the experts disagree</small>
            </span>
          </button>
        </div>
      )}
      </div>
    </>
  );
}
